import React, { useState, useRef } from 'react';
import {
  Container,
  Card,
  CardContent,
  Typography,
  Box,
  Button,
  Alert,
  LinearProgress,
  Paper,
  List,
  ListItemText,
  ListItemIcon,
  Divider,
  Chip,
  ListItem,
  ListItemButton
} from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  Upload,
  CheckCircle,
  Error,
  Warning,
  FileUpload,
  Storage,
  DataUsage,
  Download,
  Delete
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { adminService } from '@services/adminService';
import { API_BASE_URL } from '@config/api';

interface ImportResult {
  success: boolean;
  count: number;
  errors: string[];
  type: string;
}

const AdminImport: React.FC = () => {
  const navigate = useNavigate();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [importType, setImportType] = useState<'addresses' | 'companies'>('addresses');
  const [isImporting, setIsImporting] = useState(false);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);
  const [stats, setStats] = useState<any>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || 
                 file.type === 'application/vnd.ms-excel')) {
      setSelectedFile(file);
    } else {
      alert('Te rog selecteaza un fisier Excel (.xlsx sau .xls)');
    }
  };

  const handleImport = async () => {
    if (!selectedFile) {
      alert('Te rog selecteaza un fisier Excel');
      return;
    }

    setIsImporting(true);
    setImportResult(null);

    try {
      // Trimit fisierul la backend pentru import real
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('type', importType);
      
      console.log('🔍 Debug import:', {
        importType,
        fileName: selectedFile.name,
        fileSize: selectedFile.size,
        formDataEntries: Array.from(formData.entries())
      });
      
      const response = await fetch(`${API_BASE_URL}/admin/romanian-data/import`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: formData
      });
      
      const result = await response.json();
      
      if (result.success) {
        setImportResult({
          success: true,
          count: result.count || 0,
          errors: result.errors || [],
          type: importType
        });
        
        // Actualizeaza statisticile
        loadStats();
      } else {
        setImportResult({
          success: false,
          count: 0,
          errors: [result.error || 'Eroare la import'],
          type: importType
        });
      }
      
    } catch (error) {
      console.error('❌ Eroare la import:', error);
      setImportResult({
        success: false,
        count: 0,
        errors: ['Eroare la import: ' + (error as Error).message],
        type: importType
      });
    } finally {
      setIsImporting(false);
    }
  };

  const loadStats = async () => {
    try {
      const data = await adminService.getRomanianDataStats();
      setStats(data);
    } catch (error) {
      console.error('Eroare la incarcarea statisticilor:', error);
    }
  };

  React.useEffect(() => {
    loadStats();
  }, []);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            📊 Import Date Romanesti
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Importa date despre adrese si firme din fisiere Excel
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<Storage />}
          onClick={() => navigate('/admin/errors')}
        >
          Vezi Log-uri
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Sectiunea de import */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📁 Import Fisier Excel
              </Typography>
              
              {/* Selectare tip import */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Tip de date:
                </Typography>
                <Box sx={{ display: 'flex', gap: 2 }}>
                  <Button
                    variant={importType === 'addresses' ? 'contained' : 'outlined'}
                    onClick={() => setImportType('addresses')}
                    startIcon={<Storage />}
                  >
                    Adrese Romanesti
                  </Button>
                  <Button
                    variant={importType === 'companies' ? 'contained' : 'outlined'}
                    onClick={() => setImportType('companies')}
                    startIcon={<Storage />}
                  >
                    Firme Romanesti
                  </Button>
                </Box>
              </Box>

              {/* Upload fisier */}
              <Box sx={{ mb: 3 }}>
                <input
                  accept=".xlsx,.xls"
                  style={{ display: 'none' }}
                  id="file-upload"
                  type="file"
                  onChange={handleFileSelect}
                />
                <label htmlFor="file-upload">
                  <Button
                    variant="outlined"
                    component="span"
                    startIcon={<FileUpload />}
                    fullWidth
                    sx={{ py: 2 }}
                  >
                    {selectedFile ? selectedFile.name : 'Selecteaza fisier Excel'}
                  </Button>
                </label>
              </Box>

              {/* Buton import */}
              <Button
                variant="contained"
                fullWidth
                startIcon={<Upload />}
                onClick={handleImport}
                disabled={!selectedFile || isImporting}
                sx={{ py: 1.5 }}
              >
                {isImporting ? 'Import in curs...' : 'Import Date'}
              </Button>

              {/* Progress bar */}
              {isImporting && (
                <Box sx={{ mt: 2 }}>
                  <LinearProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Import in curs... Va rugam asteptati.
                  </Typography>
                </Box>
              )}

              {/* Rezultat import */}
              {importResult && (
                <Box sx={{ mt: 3 }}>
                  <Alert 
                    severity={importResult.success ? 'success' : 'error'}
                    icon={importResult.success ? <CheckCircle /> : <Error />}
                  >
                    <Typography variant="h6">
                      {importResult.success ? 'Import reusit!' : 'Import esuat!'}
                    </Typography>
                    <Typography variant="body2">
                      {importResult.success 
                        ? `${importResult.count} inregistrari importate cu succes`
                        : importResult.errors.join(', ')
                      }
                    </Typography>
                  </Alert>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Statistici */}
        <Grid size={{ xs: 12, md: 4 }}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                📈 Statistici Date
              </Typography>
              
              {stats ? (
                <Box>
                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" color="primary">
                      Adrese Romanesti
                    </Typography>
                    <Typography variant="h4" color="primary">
                      {stats.addresses?.total || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.addresses?.judete || 0} judete, {stats.addresses?.localitati || 0} localitati
                    </Typography>
                  </Paper>

                  <Paper sx={{ p: 2, mb: 2 }}>
                    <Typography variant="subtitle2" color="secondary">
                      Firme Romanesti
                    </Typography>
                    <Typography variant="h4" color="secondary">
                      {stats.companies?.total || 0}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {stats.companies?.active || 0} active, {stats.companies?.judete || 0} judete
                    </Typography>
                  </Paper>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Ultima actualizare: {stats.summary?.last_updated || 'N/A'}
                    </Typography>
                  </Box>
                </Box>
              ) : (
                <Box sx={{ textAlign: 'center', py: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Se incarca statisticile...
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>

          {/* Actiuni rapide */}
          <Card sx={{ mt: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                ⚡ Actiuni Rapide
              </Typography>
              <List dense>
                <ListItemButton  onClick={() => navigate('/admin/errors')}>
                  <ListItemIcon>
                    <DataUsage />
                  </ListItemIcon>
                  <ListItemText primary="Vezi Log-uri" />
                </ListItemButton>
                <ListItemButton  onClick={() => navigate('/admin/import')}>
                  <ListItemIcon>
                    <Storage />
                  </ListItemIcon>
                  <ListItemText primary="Import Date" />
                </ListItemButton>
                <ListItemButton >
                  <ListItemIcon>
                    <Download />
                  </ListItemIcon>
                  <ListItemText primary="Export Date" />
                </ListItemButton>
                <ListItemButton >
                  <ListItemIcon>
                    <Delete />
                  </ListItemIcon>
                  <ListItemText primary="Curata Duplicate" />
                </ListItemButton>
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};

export default AdminImport; 
